import { InputNumber, Input, Select, Button, Tooltip, App } from 'antd';
import { useMemo } from 'react';
import { parseBoothNumbers, getOccupiedByOthersBoothSet, isBoothBenefitType, hasAvailableBoothNumbers, BOOTH_EMPTY_OPTION_VALUE } from '../utils/boothNumbers';
import { parseRealizationFiles } from '../../BenefitRealizationModal/realizationTransform';
import { PRICE_REFERENCE_TIP } from '../../../constants';
import BenefitTags from './BenefitTags';

/**
 * 格式化权益总量展示：0 表示无限制
 * @param {number|null|undefined} quantity 权益总量
 * @returns {string|number}
 */
function formatTotalQuantity(quantity) {
    if (quantity == null) return '--';
    if (quantity === 0) return '无限制';
    return quantity;
}

/**
 * 展示权益总量与已售数量（与选择权益弹窗取数逻辑一致）
 * @param {Object} props
 * @param {number|null} [props.totalQuantity] 权益总量，对应权益池 quantity
 * @param {number|null} [props.soldCount] 已售数量
 */
function BenefitQuantitySummary({ totalQuantity, soldCount }) {
    if (totalQuantity == null && soldCount == null) return null;
    return (
        <div className="benefit-quantity-summary">
            权益总量：{formatTotalQuantity(totalQuantity)}，已售数量：{soldCount ?? '--'}
        </div>
    );
}

/**
 * 单条权益行 - 展示权益名称、购买数量/展位号、说明、移除按钮
 * @param {Object} props
 * @param {Object}   props.benefit       权益数据（含 realizationFiles 字段）
 * @param {Function} props.onChange      权益数据变化回调
 * @param {Function} props.onRemove      移除回调（调用前已弹出确认）
 * @param {boolean}  [props.isEditMode]  是否为编辑模式（影响移除确认文案）
 * @param {Object}   [props.errors]      校验错误 { quantity?, boothNumbers? }
 */
export default function BenefitRow({ benefit, onChange, onRemove, isEditMode = false, errors = {} }) {
    const { modal } = App.useApp();
    const isBoothType = isBoothBenefitType(benefit);
    const hasBoothPool = hasAvailableBoothNumbers(benefit);

    const selectedBooths = useMemo(() => parseBoothNumbers(benefit.boothNumbers), [benefit.boothNumbers]);

    /**
     * 已被其他机构占用的展位集合。
     * soldBoothList 含本机构已保存展位，需用 originalBoothNumbers 排除，避免取消勾选后无法再次选择。
     */
    const occupiedByOthersSet = useMemo(() => getOccupiedByOthersBoothSet(benefit.soldBoothList, benefit.originalBoothNumbers), [benefit.soldBoothList, benefit.originalBoothNumbers]);

    /**
     * 合并可选展位与已选展位；无配置时展示禁用占位项提示用户去配置
     */
    const boothOptions = useMemo(() => {
        if (!isBoothType) return [];
        const available = benefit.availableBoothNumbers || [];
        const merged = [...new Set([...selectedBooths, ...available])];
        if (merged.length === 0) {
            return [{ label: '未配置展位号，请在"权益管理"中配置', value: BOOTH_EMPTY_OPTION_VALUE, disabled: true }];
        }
        return merged.map(num => ({
            label: num,
            value: num,
            disabled: occupiedByOthersSet.has(num),
        }));
    }, [isBoothType, benefit.availableBoothNumbers, selectedBooths, occupiedByOthersSet]);

    /**
     * 展位下拉选项渲染：被其他机构占用的展位展示红色「已有机构选择」提示
     */
    const renderBoothOption = option => {
        if (option.value === BOOTH_EMPTY_OPTION_VALUE) {
            return <span className="benefit-booth-option-empty">{option.label}</span>;
        }
        const isSold = occupiedByOthersSet.has(option.value);
        return (
            <div className={`benefit-booth-option${isSold ? ' benefit-booth-option--sold' : ''}`}>
                <span>{option.label}</span>
                {isSold && <span className="benefit-booth-option-sold-tip">已有机构选择</span>}
            </div>
        );
    };

    /**
     * 移除前二次确认：
     * - 编辑模式且 realizationFiles.images 非空 → "该权益已兑现，确认移除该权益？"
     * - 其他                          → "确认移除该权益？"
     */
    const handleRemove = () => {
        const rf = parseRealizationFiles(benefit.realizationFiles);
        const hasRealization = (rf?.images?.length ?? 0) > 0;
        const content = isEditMode && hasRealization ? '该权益已兑现，确认移除该权益？' : '确认移除该权益？';
        modal.confirm({
            title: '提示',
            content,
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => onRemove(),
        });
    };

    return (
        <div className="benefit-row">
            <div className="benefit-row-header">
                <span className="benefit-row-name">
                    权益：{benefit.benefitName}
                    <span style={{ marginLeft: 4, display: 'inline-flex', verticalAlign: 'middle' }}>
                        <BenefitTags isBoothBenefit={benefit.isBoothBenefit} builtin={benefit.builtin} />
                    </span>
                </span>
                <Button type="link" danger size="small" onClick={handleRemove} className="benefit-row-remove">
                    <i className="bi bi-x-circle" />
                </Button>
            </div>
            <div className="benefit-row-desc">
                <div className="benefit-row-desc-line">
                    <span className="benefit-row-desc-label">权益说明：</span>
                    <span className="benefit-row-desc-value">{benefit.benefitDesc || '-'}</span>
                </div>
                <div className="benefit-row-desc-line">
                    <span className="benefit-row-desc-label">
                        价格参照
                        {benefit.builtin !== 0 && (
                            <Tooltip title={PRICE_REFERENCE_TIP}>
                                <i className="bi bi-question-circle-fill benefit-desc-tip-icon" />
                            </Tooltip>
                        )}
                        ：
                    </span>
                    <span className="benefit-row-desc-value">{benefit.priceReference || '-'}</span>
                </div>
            </div>
            <div className="benefit-row-fields">
                {isBoothType ? (
                    <div className="benefit-row-field">
                        <label className="benefit-field-label">
                            <span className="benefit-required-mark">*</span>展位：
                        </label>
                        <div className="benefit-field-control">
                            <Select
                                mode="multiple"
                                placeholder={hasBoothPool ? '请选择展位编号' : '未配置展位号，请在"权益管理"中配置'}
                                value={selectedBooths}
                                onChange={vals => onChange({ ...benefit, boothNumbers: vals.filter(v => v !== BOOTH_EMPTY_OPTION_VALUE).join(',') })}
                                options={boothOptions}
                                optionRender={renderBoothOption}
                                style={{ width: '100%' }}
                                status={errors.boothNumbers ? 'error' : undefined}
                            />
                            {errors.boothNumbers && <div className="benefit-field-error">{errors.boothNumbers}</div>}
                            <BenefitQuantitySummary totalQuantity={benefit.totalQuantity} soldCount={benefit.soldCount} />
                        </div>
                    </div>
                ) : (
                    <div className="benefit-row-field">
                        <label className="benefit-field-label">
                            <span className="benefit-required-mark">*</span>购买数量：
                        </label>
                        <div className="benefit-field-control">
                            <InputNumber placeholder="请填写购买数量，仅可填写整数。" precision={0} min={0} value={benefit.quantity} onChange={val => onChange({ ...benefit, quantity: val })} style={{ width: '100%' }} status={errors.quantity ? 'error' : undefined} />
                            {errors.quantity && <div className="benefit-field-error">{errors.quantity}</div>}
                            <BenefitQuantitySummary totalQuantity={benefit.totalQuantity} soldCount={benefit.soldCount} />
                        </div>
                    </div>
                )}
                <div className="benefit-row-field">
                    <label className="benefit-field-label">说明：</label>
                    <div className="benefit-field-control">
                        <Input placeholder="补充说明" maxLength={1000} value={benefit.remark || ''} onChange={e => onChange({ ...benefit, remark: e.target.value })} />
                    </div>
                </div>
            </div>
        </div>
    );
}
